import { Link } from 'react-router-dom';
import { Shield, FileText, Users, Scale, AlertTriangle, CheckCircle, Building2, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const RuleSection = ({ icon: Icon, title, children }) => (
  <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 md:p-8">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-[#2a2a2a] rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-amber-400" />
      </div>
      <div className="flex-1">
        <h3 className="font-manrope font-semibold text-lg text-white mb-4">{title}</h3>
        <div className="text-gray-400 leading-relaxed space-y-3">{children}</div>
      </div>
    </div>
  </div>
);

export const ReglasMarcaPage = () => {
  return (
    <div className="page-transition bg-[#0a0a0a] min-h-screen" data-testid="reglas-marca-page">
      {/* Hero Section */}
      <div className="relative bg-[#111111] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1770200574989-a4cca2c70c00?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Planos arquitectónicos"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/60 to-[#111111]" />
        </div>
        <div className="container-cni relative py-16 md:py-24 text-center">
          <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-6">
            <Shield className="w-4 h-4 mr-2" />
            Reglamento Oficial
          </Badge>
          <h1 className="font-manrope font-bold text-3xl md:text-5xl text-white mb-6">
            Reglas de Uso de la<br />Marca Colectiva
          </h1>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Reglamento de uso de la marca colectiva "COLEGIO NACIONAL DE INMOBILIARIOS" 
            para garantizar los más altos estándares de calidad y profesionalismo en el 
            sector inmobiliario mexicano.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container-cni py-16">
        {/* Key Points */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] text-white rounded-xl p-6 text-center">
            <Shield className="w-10 h-10 mx-auto mb-4 text-amber-400" />
            <h3 className="font-manrope font-semibold text-lg mb-2">Marca Protegida</h3>
            <p className="text-gray-400 text-sm">Registrada ante el IMPI con los más altos estándares legales</p>
          </div>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] text-white rounded-xl p-6 text-center">
            <Users className="w-10 h-10 mx-auto mb-4 text-amber-400" />
            <h3 className="font-manrope font-semibold text-lg mb-2">Uso Exclusivo</h3>
            <p className="text-gray-400 text-sm">Solo para miembros autorizados que cumplan los requisitos</p>
          </div>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] text-white rounded-xl p-6 text-center">
            <Award className="w-10 h-10 mx-auto mb-4 text-amber-400" />
            <h3 className="font-manrope font-semibold text-lg mb-2">Calidad Garantizada</h3>
            <p className="text-gray-400 text-sm">Nivel de excelencia supervisado por el Consejo Directivo</p>
          </div>
        </div>

        {/* Rules Sections */}
        <div className="space-y-6">
          <RuleSection icon={Building2} title="1. Titularidad de la Marca">
            <p className="text-gray-300">
              El Colegio Nacional de Inmobiliarios, como asociación civil sin fines de lucro, será titular de todos los derechos 
              derivados de la marca <strong className="text-white">"COLEGIO NACIONAL DE INMOBILIARIOS"</strong> ("La Marca"), su uso y su registro.
            </p>
            <p className="text-gray-300">
              La marca se aplica a servicios de educación, formación, entretenimiento, actividades deportivas y culturales, 
              así como a la capacitación, formación y distinción de los profesionales inmobiliarios.
            </p>
          </RuleSection>

          <RuleSection icon={FileText} title="2. Uso de la Marca">
            <p className="text-gray-300">
              El Colegio y sus miembros autorizados podrán usar La Marca en relación con los servicios amparados, ya sea de manera conjunta o individual.
            </p>
            <p className="text-gray-300">
              En caso de uso individual por parte de los miembros, estos deberán mantener el alto nivel de calidad 
              de los servicios establecido por el Colegio Nacional de Inmobiliarios.
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <p className="text-amber-400 text-sm">
                <strong>Importante:</strong> La autorización para el uso de La Marca estará supeditada a la aprobación 
                por escrito del Consejo Directivo.
              </p>
            </div>
          </RuleSection>

          <RuleSection icon={CheckCircle} title="3. Requisitos para Usar la Marca">
            <p className="mb-4">Para pertenecer al Colegio y utilizar la marca, los profesionales deben cumplir con:</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span><strong>Certificación federal</strong> avalada por los estatutos del Colegio</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span><strong>Certificación estatal</strong> registrada ante la Secretaría de Economía o SEDECO correspondiente</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span><strong>Diplomado o dos cursos</strong> de formación inmobiliaria avalados por el CNI</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span><strong>Cumplimiento</strong> de los estatutos y lineamientos del Colegio</span>
              </li>
            </ul>
          </RuleSection>

          <RuleSection icon={Users} title="4. Consejo Directivo y Asociaciones">
            <p>
              El Consejo Directivo estará compuesto por las asociaciones que se adhieran al Colegio Nacional de Inmobiliarios. 
              Este órgano es responsable de:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
              <li>Garantizar el cumplimiento de las reglas de uso y reglamentos</li>
              <li>Aprobar o denegar el uso de La Marca a nuevos miembros</li>
              <li>Supervisar el nivel de calidad de los servicios</li>
              <li>Resolver conflictos relacionados con La Marca</li>
              <li><strong className="text-white">Supervisar el funcionamiento general del Colegio</strong></li>
              <li><strong className="text-white">Aprobar la oferta académica</strong></li>
              <li><strong className="text-white">Tomar decisiones sobre el Colegiado</strong></li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800 text-sm">
                <strong>El Colegiado</strong> se define como la agrupación de profesionales inmobiliarios que forman parte 
                de esta asociación civil sin fines de lucro. Las asociaciones que se incorporen al Consejo Directivo 
                deberán formar parte del acta constitutiva del Colegio.
              </p>
            </div>
          </RuleSection>

          <RuleSection icon={Scale} title="5. Licencias">
            <p>
              En caso de otorgamiento de licencias, el Consejo Directivo deberá pactar previamente, 
              de común acuerdo, las bases de los contratos respectivos, definiendo:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Exclusividad</p>
                <p className="text-xs text-slate-500">Grado definido</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Territorio</p>
                <p className="text-xs text-slate-500">Área geográfica</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Duración</p>
                <p className="text-xs text-slate-500">Vigencia</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Regalías</p>
                <p className="text-xs text-slate-500">Fondo del proyecto</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Los ingresos por regalías se destinarán exclusivamente al fondo del proyecto para impulsar 
              el crecimiento del Colegio Nacional de Inmobiliarios.
            </p>
          </RuleSection>

          <RuleSection icon={AlertTriangle} title="6. Incumplimiento y Sanciones">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">
                El incumplimiento de cualquiera de los requisitos establecidos resultará en la 
                <strong> pérdida de autorización para usar La Marca</strong>.
              </p>
            </div>
            <p className="mt-4">
              Se aplicarán procedimientos legales conforme a la <strong>Ley Federal de Protección a la Propiedad Industrial</strong> 
              en caso de uso indebido de la marca.
            </p>
          </RuleSection>

          <RuleSection icon={Building2} title="7. Incorporación de Nuevas Asociaciones">
            <p>
              El Colegio Nacional de Inmobiliarios contempla la incorporación de nuevas asociaciones interesadas 
              en formar parte del proyecto.
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <p className="text-amber-300 text-sm">
                <strong>Al pactarse la incorporación</strong>, las asociaciones podrán sumarse y serán parte del 
                <strong> Consejo Directivo del Colegio</strong>, donde en conjunto supervisarán:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-amber-200 text-sm">
                <li>El funcionamiento general del Colegio</li>
                <li>La oferta académica a aprobar</li>
                <li>Las decisiones sobre el Colegiado</li>
              </ul>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800 text-sm">
                Las asociaciones que deseen utilizar La Marca deberán cumplir con los requisitos de calidad, 
                profesionalismo y ética establecidos. Su incorporación implica formar parte del 
                <strong> acta constitutiva</strong> de esta asociación civil sin fines de lucro.
              </p>
            </div>
          </RuleSection>
        </div>

        {/* CTA */}
        <div className="mt-16 relative bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1637665662134-db459c1bbb46?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
              alt="Sala de juntas"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="relative p-8 md:p-12 text-center">
            <h2 className="font-manrope font-bold text-2xl text-white mb-4">
              ¿Deseas formar parte del Colegio?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Conoce los requisitos de formación y comienza tu proceso de certificación para 
              obtener el uso autorizado de la marca.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/rangos">
                <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
                  <Award className="w-4 h-4 mr-2" />
                  Ver Rangos
                </Button>
              </Link>
              <Link to="/oferta-academica">
                <Button className="bg-[#333333] hover:bg-[#444444] text-white font-bold border border-[#555555]">
                  Ver Oferta Académica
                </Button>
              </Link>
              <Link to="/contacto">
                <Button className="bg-[#333333] hover:bg-[#444444] text-white font-bold border border-[#555555]">
                  Contactar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReglasMarcaPage;
